import React, { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FilterX } from "lucide-react";
import { SliderMulti as Slider } from "@/components/ui/slider-multi";

export function TourFilterSidebar({ filters, onFilterChange, className, minMaxValues, availableActivities, availableMonths }) {

    const [priceRange, setPriceRange] = useState(filters.priceRange || [0, 100000]);
    const [durationRange, setDurationRange] = useState(filters.durationRange || [0, 30]);
    const [groupSizeRange, setGroupSizeRange] = useState(filters.groupSizeRange || [0, 50]);

    useEffect(() => {
        setPriceRange(filters.priceRange || minMaxValues.price);
    }, [filters.priceRange, minMaxValues.price]);

    useEffect(() => {
        setDurationRange(filters.durationRange || minMaxValues.duration);
    }, [filters.durationRange, minMaxValues.duration]);

    useEffect(() => {
        setGroupSizeRange(filters.groupSizeRange || minMaxValues.groupSize);
    }, [filters.groupSizeRange, minMaxValues.groupSize]);


    const handleActivityChange = (activity, checked) => {
        const current = filters.activities || [];
        const updated = checked
            ? [...current, activity]
            : current.filter((a) => a !== activity);
        onFilterChange("activities", updated);
    };

    const handleDepartureTypeChange = (type, checked) => {
        const current = filters.departureTypes || [];
        const updated = checked
            ? [...current, type]
            : current.filter((t) => t !== type);
        onFilterChange("departureTypes", updated);
    };

    const handleMonthChange = (month, checked) => {
        const current = filters.departureMonths || [];
        const updated = checked
            ? [...current, month]
            : current.filter((m) => m !== month);
        onFilterChange("departureMonths", updated);
    };

    const clearFilters = () => {
        onFilterChange("reset", null);
    };

    const hasFilters =
        (filters.activities?.length > 0) ||
        (filters.departureTypes?.length > 0) ||
        (filters.departureMonths?.length > 0) ||
        (filters.priceRange && (filters.priceRange[0] > minMaxValues.price[0] || filters.priceRange[1] < minMaxValues.price[1])) ||
        (filters.durationRange && (filters.durationRange[0] > minMaxValues.duration[0] || filters.durationRange[1] < minMaxValues.duration[1])) ||
        (filters.groupSizeRange && (filters.groupSizeRange[0] > minMaxValues.groupSize[0] || filters.groupSizeRange[1] < minMaxValues.groupSize[1]));


    return (
        <div className={`space-y-6 ${className}`}>
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Filters</h3>
                {hasFilters && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="h-8 px-2 text-xs text-muted-foreground hover:text-destructive"
                    >
                        <FilterX className="h-3.5 w-3.5 mr-1" />
                        Clear
                    </Button>
                )}
            </div>

            <Accordion type="multiple" defaultValue={["price", "duration", "groupSize", "departure", "months", "activities"]} className="w-full">
                {/* Price Filter */}
                <AccordionItem value="price">
                    <AccordionTrigger className="text-sm font-medium py-3">
                        Price Range
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 px-2">
                        <Slider
                            defaultValue={minMaxValues.price}
                            value={priceRange}
                            min={minMaxValues.price[0]}
                            max={minMaxValues.price[1]}
                            step={100}
                            onValueChange={setPriceRange}
                            onValueCommit={(val) => onFilterChange("priceRange", val)}
                            className="mb-6"
                        />
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>NPR {priceRange[0].toLocaleString()}</span>
                            <span>NPR {priceRange[1].toLocaleString()}</span>
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Duration Filter */}
                <AccordionItem value="duration">
                    <AccordionTrigger className="text-sm font-medium py-3">
                        Duration (Days)
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 px-2">
                        <Slider
                            defaultValue={minMaxValues.duration}
                            value={durationRange}
                            min={minMaxValues.duration[0]}
                            max={minMaxValues.duration[1]}
                            step={1}
                            onValueChange={setDurationRange}
                            onValueCommit={(val) => onFilterChange("durationRange", val)}
                            className="mb-6"
                        />
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{durationRange[0]} Days</span>
                            <span>{durationRange[1]} Days</span>
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Group Size Filter */}
                <AccordionItem value="groupSize">
                    <AccordionTrigger className="text-sm font-medium py-3">
                        Group Size
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 px-2">
                        <Slider
                            defaultValue={minMaxValues.groupSize}
                            value={groupSizeRange}
                            min={minMaxValues.groupSize[0]}
                            max={minMaxValues.groupSize[1]}
                            step={1}
                            onValueChange={setGroupSizeRange}
                            onValueCommit={(val) => onFilterChange("groupSizeRange", val)}
                            className="mb-6"
                        />
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{groupSizeRange[0]} Person</span>
                            <span>{groupSizeRange[1]} Person</span>
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Departure Type Filter, IsGroup */}
                <AccordionItem value="departure">
                    <AccordionTrigger className="text-sm font-medium py-3">
                        Departure Type
                    </AccordionTrigger>
                    <AccordionContent className="px-2 space-y-3 pt-2">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="dept-custom"
                                checked={filters.departureTypes?.includes('custom')}
                                onCheckedChange={(checked) => handleDepartureTypeChange('custom', checked)}
                            />
                            <Label
                                htmlFor="dept-custom"
                                className="text-xs font-normal cursor-pointer leading-tight"
                            >
                                Custom Departure Date
                            </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="dept-fixed"
                                checked={filters.departureTypes?.includes('fixed')}
                                onCheckedChange={(checked) => handleDepartureTypeChange('fixed', checked)}
                            />
                            <Label
                                htmlFor="dept-fixed"
                                className="text-xs font-normal cursor-pointer leading-tight"
                            >
                                Fix Departure Date
                            </Label>
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Departure Months Filter */}
                <AccordionItem value="months">
                    <AccordionTrigger className="text-sm font-medium py-3">
                        Departure Months
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-3 pt-1 max-h-60 overflow-y-auto pr-2">
                            {availableMonths?.length === 0 && <span className="text-xs text-muted-foreground">No departures found</span>}
                            {availableMonths?.map((month) => (
                                <div key={month} className="flex items-start space-x-2">
                                    <Checkbox
                                        id={`month-${month}`}
                                        checked={filters.departureMonths?.includes(month)}
                                        onCheckedChange={(checked) =>
                                            handleMonthChange(month, checked)
                                        }
                                        className="mt-0.5"
                                    />
                                    <Label
                                        htmlFor={`month-${month}`}
                                        className="text-xs font-normal cursor-pointer leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                        {month}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </AccordionContent>
                </AccordionItem>

                {/* Activities Filter */}
                {/* <AccordionItem value="activities">
                    <AccordionTrigger className="text-sm font-medium py-3">
                        Activities
                    </AccordionTrigger>
                    <AccordionContent>
                        <div className="space-y-3 pt-1 max-h-60 overflow-y-auto pr-2">
                            {availableActivities?.length === 0 && <span className="text-xs text-muted-foreground">No activities found</span>}
                            {availableActivities?.map((activity) => (
                                <div key={activity} className="flex items-start space-x-2">
                                    <Checkbox
                                        id={`activity-${activity}`}
                                        checked={filters.activities?.includes(activity)}
                                        onCheckedChange={(checked) =>
                                            handleActivityChange(activity, checked)
                                        }
                                        className="mt-0.5"
                                    />
                                    <Label
                                        htmlFor={`activity-${activity}`}
                                        className="text-xs font-normal cursor-pointer leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                    >
                                        {activity}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </AccordionContent>
                </AccordionItem> */}
            </Accordion>
        </div>
    );
}
